import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Flex, Text } from '@chakra-ui/react'
import { IoTDeviceDirection, IotDeviceFunction, IotOutputFunction, SublocationWithDevices } from '@leid/api-locations'
import { OrderItem } from '@leid/api-orders'

import { LeidYellowButton } from 'components/ui/Buttons/Button'
import { NewOpenDoorIcon } from 'components/ui/ItemCard/styles'
import LeidModal from 'components/ui/Modal/LeidModal'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import { DoorOutputConfig } from './models'

type Props = {
  isOpen: boolean
  onClose: () => void
  orderItem: OrderItem
  devices: SublocationWithDevices[]
}

const OpenDoorsModal = ({ isOpen, onClose, orderItem, devices }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [outDoors, setOutDoors] = useState<DoorOutputConfig[]>([])

  useEffect(() => {
    if (!isOpen) {
      resetToDefaultState()
      return
    }

    searchOutDoors()
  }, [isOpen])

  const resetToDefaultState = () => {
    setOutDoors([])
  }

  const searchOutDoors = () => {
    const outDoors: DoorOutputConfig[] = []

    for (const sublocationData of devices) {
      const doorDevices = sublocationData.devices.filter((s) => s.function === IotDeviceFunction.DOOR)
      for (const door of doorDevices) {
        const outputs = door.outputs
          .filter(
            (s) =>
              s.function === IotOutputFunction.DOOR &&
              (s.direction === IoTDeviceDirection.OUT || s.direction === IoTDeviceDirection.BOTH) &&
              s.allowedToTriggerFromEcommerce === true,
          )
          .map<DoorOutputConfig>((s) => ({
            id: s.id,
            name: s.name,
            sublocationId: sublocationData.sublocationId,
          }))

        outDoors.push(...outputs)
      }
    }

    setOutDoors(outDoors)
  }

  const triggerOutput = async (sublocationId: number, gateId: number) => {
    const triggeredResponse = await apiClient.triggerSubLocationOutput({
      locationId: orderItem.locationId ?? 0,
      sublocationId: sublocationId,
      ioId: gateId ?? 0,
      userId: 0,
      orderId: orderItem.orderId,
      customerId: orderItem.customerId,
    })

    if (!triggeredResponse.success) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      console.error('Failed to trigger output')
      return
    }
    onClose()
  }

  return (
    <LeidModal isOpen={isOpen} onClose={onClose} header={t('openLocation')}>
      <Flex flexDirection="column" justifyContent=" center" alignItems="center" gridGap="12px">
        {outDoors.map((door) => (
          <LeidYellowButton
            key={door.id}
            onClick={() => {
              triggerOutput(door.sublocationId, door.id)
            }}
          >
            <NewOpenDoorIcon />
            <Text>{door.name}</Text>
          </LeidYellowButton>
        ))}
      </Flex>
    </LeidModal>
  )
}

export default OpenDoorsModal
